import { useEffect, useState } from "react";
import { Card } from "../ui/Card.tsx";
import { Button } from "../ui/Button.tsx";
import { Input } from "../ui/Input.tsx";
import { Label } from "../ui/Label.tsx";
import {
  Plus, Search, Pencil, Trash2,
  ChevronLeft, ChevronRight, Database, Users, CheckCircle2
} from "lucide-react";
import { toast } from "react-toastify";
import { createPortal } from "react-dom";
import SkeletonLoader from "../ui/SkeletonLoader";
import {
  getGroups, createGroup, updateGroup, deleteGroup,
  getGroupById, getGroupDBOptions, getGroupTableOptions,
} from "../../apiGroups";

const defaultFormData = { name: "", description: "" };

export default function GroupsTab({ onGroupsChange }) {

  // ── State ──────────────────────────────────────────────────────────────────
  const [groups, setGroups]             = useState([]);
  const [searchTerm, setSearchTerm]     = useState("");
  const [loading, setLoading]           = useState(true);
  const [showForm, setShowForm]         = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [formData, setFormData]         = useState({ ...defaultFormData });
  const [errors, setErrors]             = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep]                 = useState(1);
  const [dbOptions, setDbOptions]       = useState([]);
  const [selectedDBs, setSelectedDBs]   = useState([]);
  const [activeDBId, setActiveDBId]     = useState(null);
  const [tableLoading, setTableLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 4;

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const res = await getGroups();
      setGroups(res.data || []);
    } catch { toast.error("Failed to load groups"); }
    finally { setLoading(false); }
  };

  const fetchDBOptions = async () => {
    try {
      const res = await getGroupDBOptions();
      setDbOptions(res.data || []);
    } catch { toast.error("Failed to load databases"); }
  };

  useEffect(() => { fetchGroups(); }, []);
  useEffect(() => { if (showForm) fetchDBOptions(); }, [showForm]);

  
  // ── Derived ────────────────────────────────────────────────────────────────

  const filteredGroups = groups.filter(g =>
    g.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (g.description || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredGroups.length / rowsPerPage);
  const paginatedGroups = filteredGroups.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const activeDB = selectedDBs.find(d => d.id === activeDBId) || null;
useEffect(() => {
  setCurrentPage(1);
}, [searchTerm]);

useEffect(() => {
  if (currentPage > totalPages) {
    setCurrentPage(p => Math.min(p, totalPages || 1));
  }
}, [filteredGroups, totalPages]);
  // ── Validation ─────────────────────────────────────────────────────────────

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Group name is required";
    else if (formData.name.trim().length < 2) newErrors.name = "Name must be at least 2 characters";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Handlers ───────────────────────────────────────────────────────────────

  
  const handleCancel = () => {
    setShowForm(false);
    setEditingGroup(null);
    setFormData({ ...defaultFormData });
    setSelectedDBs([]);
    setActiveDBId(null);
    setStep(1);
    setErrors({});
  };

  const handleEdit = async (group) => {
    try {
      const res = await getGroupById(group.id);
      setEditingGroup(group);
      setFormData({ name: res.data.name, description: res.data.description || "" });

      // Enrich each assigned DB with full table options so unchecking works correctly
      const enriched = await Promise.all(
        (res.data.assigned_databases || []).map(async (db) => {
          const tableRes = await getGroupTableOptions(db.id, group.id);
          return {
            id:               db.id,
            title:            db.title,
            subtitle:         db.subtitle || "",
            assigned_tables:  tableRes.data.assigned_tables || [],
            available_tables: tableRes.data.available_tables || [],
          };
        })
      );

      setSelectedDBs(enriched);
      setActiveDBId(enriched[0]?.id || null);
      setStep(1);
      setShowForm(true);
    } catch { toast.error("Failed to load group details"); }
  };

  const handleDelete = async (group) => {
    if (!window.confirm(`Delete "${group.name}"? This cannot be undone.`)) return;
    try {
      await deleteGroup(group.id);
      toast.success("Group deleted");
      setGroups(prev => prev.filter(g => g.id !== group.id));
      // Refresh groups list in parent component
      if (onGroupsChange) {
        onGroupsChange();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
    }
  };

  const handleAddDB = async (db) => {
    if (selectedDBs.some(d => d.id === db.id)) { setActiveDBId(db.id); return; }
    setTableLoading(true);
    try {
      const res = await getGroupTableOptions(db.id, editingGroup?.id || null);
      setSelectedDBs(prev => [...prev, {
        id:               db.id,
        title:            db.title,
        subtitle:         db.subtitle || "",
        assigned_tables:  res.data.assigned_tables || [],
        available_tables: res.data.available_tables || [],
      }]);
      setActiveDBId(db.id);
    } catch { toast.error("Failed to load tables"); }
    finally { setTableLoading(false); }
  };

  const handleRemoveDB = (dbId) => {
    setSelectedDBs(prev => prev.filter(d => d.id !== dbId));
    if (activeDBId === dbId) setActiveDBId(null);
  };

  const handleToggleTable = (table) => {
    setSelectedDBs(prev => prev.map(db => {
      if (db.id !== activeDBId) return db;
      const isAssigned = db.assigned_tables.includes(table);
      return {
        ...db,
        assigned_tables:  isAssigned ? db.assigned_tables.filter(t => t !== table) : [...db.assigned_tables, table],
        available_tables: isAssigned ? [...db.available_tables, table] : db.available_tables.filter(t => t !== table),
      };
    }));
  };

  const handleSelectAll = () => {
    if (!activeDB) return;
    const all = [...activeDB.assigned_tables, ...activeDB.available_tables];
    setSelectedDBs(prev => prev.map(db =>
      db.id !== activeDBId ? db : { ...db, assigned_tables: all, available_tables: [] }
    ));
  };

  const handleClearAll = () => {
    if (!activeDB) return;
    const all = [...activeDB.assigned_tables, ...activeDB.available_tables];
    setSelectedDBs(prev => prev.map(db =>
      db.id !== activeDBId ? db : { ...db, assigned_tables: [], available_tables: all }
    ));
  };

  // ── Step 1: Next ───────────────────────────────────────────────────────────
  const handleNextStep = (e) => {
    e.preventDefault();
    if (validateStep1()) setStep(2);
  };

  // ── Step 2: Final Submit ───────────────────────────────────────────────────
  const handleFinalSubmit = async (e) => {
    e.preventDefault();

    if (selectedDBs.length === 0) { toast.error("Assign at least one database"); return; }
    const missing = selectedDBs.find(db => db.assigned_tables.length === 0);
    if (missing) { toast.error(`Select at least one table for "${missing.title}"`); return; }

    const payload = {
      name:               formData.name.trim(),
      description:        formData.description.trim(),
      assigned_databases: selectedDBs.map(db => ({ id: db.id, allowed_tables: db.assigned_tables })),
    };

    setIsSubmitting(true);
    try {
      if (editingGroup) {
        await updateGroup(editingGroup.id, payload);
        toast.success("Group updated successfully");
      } else {
        await createGroup(payload);
        toast.success("Group created successfully");
      }
      await fetchGroups();
      handleCancel();
      if (onGroupsChange) onGroupsChange();

    } catch (err) {
      toast.error(err.response?.data?.message || "Operation failed");
    } finally { setIsSubmitting(false); }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-3">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between gap-3">
        
        <div className="flex gap-2">
          <div className="relative w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search groups..."
              className="pl-10 h-9"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <Button
            onClick={() => setShowForm(true)}
            disabled={showForm}
            className="h-9 px-4 flex items-center gap-2 whitespace-nowrap"
          >
            <Plus size={14} />
            Add Group
          </Button>
        </div>
      </div>

      {/* ── TABLE ── */}
      <div className="bg-background">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-background shadow-sm">
            <tr className="text-left text-muted-foreground font-medium">
              <th className="px-5 py-2 w-[26%]">Group</th>
              <th className="px-5 py-2 w-[28%]">Description</th>
              <th className="px-5 py-2 w-[26%]">Databases</th>
              <th className="px-5 py-2 w-[10%]">Users</th>
              <th className="px-5 py-2 w-[10%] text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading
              ? Array.from({ length: 3 }).map((_, i) => (
                  <tr key={i}>
                    <td className="px-5 py-3"><SkeletonLoader className="h-4 w-32" /></td>
                    <td className="px-5 py-3"><SkeletonLoader className="h-4 w-40" /></td>
                    <td className="px-5 py-3"><SkeletonLoader className="h-4 w-28" /></td>
                    <td className="px-5 py-3"><SkeletonLoader className="h-4 w-8" /></td>
                    <td className="px-5 py-3 text-right"><SkeletonLoader className="h-6 w-14 ml-auto" /></td>
                  </tr>
                ))
              : paginatedGroups.map(group => (
                  <tr key={group.id} className="hover:bg-muted/40 transition-colors">

                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center text-xs font-semibold shrink-0">
                          {group.name.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium text-foreground truncate">{group.name}</span>
                      </div>
                    </td>

                    <td className="px-5 py-3 text-xs text-muted-foreground">
                      {group.description || "—"}
                    </td>

                    <td className="px-5 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(group.assigned_databases || []).slice(0, 2).map(db => (
                          <span key={db.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-medium">
                            <Database className="w-2.5 h-2.5" />
                            {db.title}
                          </span>
                        ))}
                        {(group.assigned_databases || []).length > 2 && (
                          <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[10px]">
                            +{group.assigned_databases.length - 2} more
                          </span>
                        )}
                        {(group.assigned_databases || []).length === 0 && (
                          <span className="text-xs text-muted-foreground">No DBs assigned</span>
                        )}
                      </div>
                    </td>

                    <td className="px-5 py-3">
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                        <Users className="w-3 h-3" />
                        {group.user_count}
                      </span>
                    </td>

                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button size="icon" className="h-8 w-8" variant="ghost" onClick={() => handleEdit(group)}>
                          <Pencil size={14} />
                        </Button>
                        <Button size="icon" className="h-8 w-8" variant="ghost" onClick={() => handleDelete(group)}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>

                  </tr>
                ))
            }
          </tbody>
        </table>
      </div>

      {/* ── EMPTY STATE ── */}
      
      {/* ── FOOTER: COUNT + PAGINATION ── */}
      <div className="flex items-center justify-between mt-2 px-1 min-h-[36px]">

        {/* Left: Count */}
        <p className="text-xs text-muted-foreground">
          {!loading && (
            searchTerm
              ? <><span className="font-medium text-foreground">{filteredGroups.length}</span> of {groups.length} groups</>
              : <><span className="font-medium text-foreground">{groups.length}</span> total groups</>
          )}
        </p>

        {/* Right: Pagination */}
        {totalPages > 1 ? (
          <div className="flex items-center gap-3 bg-muted/40 px-3 py-1.5 rounded-lg">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
              className={`flex items-center justify-center w-7 h-7 rounded-md transition-all
                ${currentPage === 1
                  ? "text-muted-foreground opacity-40 cursor-not-allowed"
                  : "hover:bg-primary/10 hover:text-primary"}`}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <span className="text-xs font-medium text-muted-foreground tracking-wide">
              {currentPage} / {totalPages}
            </span>

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
              className={`flex items-center justify-center w-7 h-7 rounded-md transition-all
                ${currentPage === totalPages
                  ? "text-muted-foreground opacity-40 cursor-not-allowed"
                  : "hover:bg-primary/10 hover:text-primary"}`}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div />
        )}

      </div>

      {/* ── EMPTY STATE ── */}
      {!loading && filteredGroups.length === 0 && (
        <Card className="p-8 text-center border border-dashed">
          <Users className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">
            {searchTerm ? "No groups match your search" : "No groups yet. Create one to get started."}
          </p>
        </Card>
      )}

      {/* ── MODAL ── */}
      {showForm && createPortal(
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200 backdrop-blur-sm">
          <div className="bg-background rounded-xl shadow-2xl w-full max-w-2xl h-[580px] max-h-[90vh] flex flex-col border border-border">

            {/* Header */}
            <div className="flex justify-between items-center px-6 py-4 border-b border-border shrink-0 bg-muted/10">
              <div>
                <h2 className="text-lg font-semibold tracking-tight">
                  {editingGroup ? "Edit Group" : "Create New Group"}
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {step === 1 ? "Group Details" : "Database & Table Access"}
                </p>
              </div>

              {/* Step indicator */}
              <div className="flex items-center gap-1.5 mr-3">
                {[1, 2].map(s => (
                  <div key={s} className="flex items-center gap-1">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold transition-all
                      ${step === s
                        ? "bg-primary text-white shadow-sm"
                        : step > s
                          ? "bg-green-500 text-white"
                          : "bg-muted text-muted-foreground"}`}>
                      {step > s ? "✓" : s}
                    </div>
                    {s < 2 && <div className={`w-5 h-px transition-colors ${step > s ? "bg-green-500" : "bg-border"}`} />}
                  </div>
                ))}
              </div>

              <button
                onClick={handleCancel}
                className="p-1.5 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-all"
              >
                ✕
              </button>
            </div>

            {/* Body — NO single <form> wrapping everything */}
            <div className="no-scrollbar px-6 py-5 flex-1 overflow-y-auto">

              {/* ── STEP 1 ── */}
              {step === 1 && (
                <div className="space-y-4 max-w-lg">

                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-muted-foreground">Group Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={e => {
                        setFormData({ ...formData, name: e.target.value });
                        setErrors(p => ({ ...p, name: null }));
                      }}
                      placeholder="e.g. Sales Team"
                      className={`h-9 ${errors.name ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                    />
                    {errors.name && <p className="text-[10px] text-red-500 font-medium">{errors.name}</p>}
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-muted-foreground">
                      Description <span className="opacity-40 font-normal">(Optional)</span>
                    </Label>
                    <textarea
                      value={formData.description}
                      onChange={e => setFormData({ ...formData, description: e.target.value })}
                      placeholder="What is this group used for?"
                      rows={3}
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                    />
                  </div>

                </div>
              )}

              {/* ── STEP 2 ── */}
              {step === 2 && (
                <div className="flex gap-4 h-full" style={{ minHeight: 380 }}>

                  {/* LEFT: DB selector */}
                  <div className="w-44 shrink-0 flex flex-col">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                      Databases
                    </p>
                    <div className="flex flex-col gap-1 overflow-y-auto">
                      {dbOptions.map(db => {
                        const isSelected = selectedDBs.some(d => d.id === db.id);
                        const isActive   = activeDBId === db.id;
                        const selCount   = selectedDBs.find(d => d.id === db.id)?.assigned_tables?.length || 0;

                        return (
                          <button
                            key={db.id}
                            type="button"
                            onClick={() => handleAddDB(db)}
                            className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all border
                              ${isActive
                                ? "bg-primary text-primary-foreground border-primary shadow-sm"
                                : isSelected
                                  ? "bg-primary/10 text-primary border-primary/30"
                                  : "bg-muted/30 text-foreground border-border hover:bg-muted/60"
                              }`}
                          >
                            <div className="flex items-center gap-1.5">
                              <Database className="w-3 h-3 shrink-0 opacity-70" />
                              <span className="font-medium truncate">{db.title}</span>
                            </div>
                            <div className={`text-[10px] mt-0.5 ${isActive ? "opacity-75" : "opacity-40"}`}>
                              {db.subtitle}
                            </div>
                            {isSelected && (
                              <div className={`text-[10px] mt-1 font-semibold ${isActive ? "opacity-90" : "text-primary"}`}>
                                {selCount} table{selCount !== 1 ? "s" : ""} selected
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* RIGHT: Table picker */}
                  <div className="flex-1 flex flex-col min-w-0 border-l pl-4">

                    {!activeDB ? (
                      <div className="flex-1 flex flex-col items-center justify-center text-center gap-2 text-muted-foreground">
                        <Database className="w-9 h-9 opacity-20" />
                        <p className="text-xs">Click a database on the left<br />to manage table access</p>
                      </div>

                    ) : tableLoading ? (
                      <div className="space-y-2 pt-1">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <SkeletonLoader key={i} className="h-8 w-full rounded-md" />
                        ))}
                      </div>

                    ) : (
                      <>
                        {/* DB header */}
                        <div className="flex items-start justify-between mb-3 pb-2.5 border-b">
                          <div>
                            <p className="text-sm font-semibold flex items-center gap-1.5">
                              <Database className="w-3.5 h-3.5 text-primary" />
                              {activeDB.title}
                            </p>
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              {activeDB.assigned_tables.length} of {activeDB.assigned_tables.length + activeDB.available_tables.length} tables selected
                            </p>
                          </div>
                          <div className="flex items-center gap-2 text-[10px]">
                            <button type="button" onClick={handleSelectAll} className="text-primary hover:underline">All</button>
                            <span className="text-muted-foreground">·</span>
                            <button type="button" onClick={handleClearAll} className="text-muted-foreground hover:text-foreground hover:underline">None</button>
                            <span className="text-muted-foreground">·</span>
                            <button type="button" onClick={() => handleRemoveDB(activeDB.id)} className="text-red-500 hover:text-red-600 hover:underline">Remove DB</button>
                          </div>
                        </div>

                        {/* Table checkboxes */}
                        <div className="no-scrollbar border rounded-lg p-1 flex-1 overflow-y-auto bg-muted/20">
                          {[...activeDB.assigned_tables, ...activeDB.available_tables].length === 0 ? (
                            <div className="p-4 text-center text-xs text-muted-foreground">No tables available</div>
                          ) : (
                            [...activeDB.assigned_tables, ...activeDB.available_tables].map(table => {
                              const checked = activeDB.assigned_tables.includes(table);
                              return (
                                <label key={table} className="flex items-center gap-3 px-3 py-2 hover:bg-background rounded-md cursor-pointer transition-colors group">
                                  <input
                                    type="checkbox"
                                    className="rounded border-gray-300 text-primary focus:ring-primary h-4 w-4"
                                    checked={checked}
                                    onChange={() => handleToggleTable(table)}
                                  />
                                  <span className={`text-sm flex-1 transition-colors
                                    ${checked ? "text-foreground font-medium" : "text-muted-foreground group-hover:text-foreground"}`}>
                                    {table}
                                  </span>
                                  {checked && <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />}
                                </label>
                              );
                            })
                          )}
                        </div>
                      </>
                    )}
                  </div>

                </div>
              )}

            </div>

            {/* Footer — each step has its own isolated buttons */}
            <div className="p-4 border-t border-border bg-muted/10 flex justify-between items-center shrink-0 rounded-b-xl">
              {step === 1 ? (
                <>
                  <Button type="button" variant="ghost" onClick={handleCancel} className="h-9 text-muted-foreground hover:text-foreground">
                    Cancel
                  </Button>
                  <Button type="button" onClick={handleNextStep} className="h-9 px-6">
                    Next Step <ChevronRight size={14} className="ml-1" />
                  </Button>
                </>
              ) : (
                <>
                  <Button type="button" variant="outline" onClick={() => setStep(1)} className="h-9">
                    <ChevronLeft size={14} className="mr-1" /> Back
                  </Button>
                  <Button
                    type="button"
                    onClick={handleFinalSubmit}
                    disabled={isSubmitting || selectedDBs.length === 0}
                    className={`h-9 px-6 shadow-md transition-all duration-200
                      ${(isSubmitting || selectedDBs.length === 0)
                        ? "opacity-50 cursor-not-allowed shadow-none"
                        : "hover:ring-2 hover:ring-primary/20 hover:scale-[1.02]"}`}
                  >
                    {isSubmitting ? "Saving..." : editingGroup ? "Update Group" : "Create Group"}
                  </Button>
                </>
              )}
            </div>

          </div>
        </div>,
        document.getElementById("modal-root")
      )}

    </div>
  );
}